import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import styles from "../styles/pages/returnrefundpolicy.module.css";

const ReturnRefundPolicy = () => {
    const canonicalUrl =
        "https://myonlineshopping.co.in/return-refund-policy";

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Return and Refund Policy - Mohit's Shopping App",
        description:
            "Information about returns, refunds, replacements, and cancellations for Amazon purchases made through Mohit's Shopping App.",
        url: canonicalUrl,
        isPartOf: {
            "@type": "WebSite",
            name: "Mohit's Shopping App",
            url: "https://myonlineshopping.co.in/",
        },
    };

    const sections = [
        {
            id: "purchases",
            number: "01",
            title: "Purchases Are Completed on Amazon",
        },
        {
            id: "returns",
            number: "02",
            title: "Return Requests",
        },
        {
            id: "refunds",
            number: "03",
            title: "Refunds",
        },
        {
            id: "replacement",
            number: "04",
            title: "Replacement or Damaged Products",
        },
        {
            id: "cancellation",
            number: "05",
            title: "Order Cancellation",
        },
        {
            id: "contact-amazon",
            number: "06",
            title: "Where Should You Contact?",
        },
        {
            id: "role",
            number: "07",
            title: "Our Role",
        },
        {
            id: "amazon-policies",
            number: "08",
            title: "Amazon Policies Apply",
        },
        {
            id: "support",
            number: "09",
            title: "Website Support",
        },
    ];

    return (
        <>
            <Helmet>
                <title>
                    Return & Refund Policy | Mohit's Shopping App
                </title>

                <meta
                    name="description"
                    content="Learn how returns, refunds, replacements, and cancellations work for products purchased through Amazon links on Mohit's Shopping App."
                />

                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large"
                />

                <meta name="author" content="Mohit Singh" />

                <link
                    rel="canonical"
                    href={canonicalUrl}
                />

                <meta
                    property="og:type"
                    content="website"
                />

                <meta
                    property="og:title"
                    content="Return & Refund Policy | Mohit's Shopping App"
                />

                <meta
                    property="og:description"
                    content="Information about Amazon returns, refunds, replacements, and cancellations for purchases made through Mohit's Shopping App."
                />

                <meta
                    property="og:url"
                    content={canonicalUrl}
                />

                <meta
                    property="og:image"
                    content="https://myonlineshopping.co.in/favicon.png"
                />

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content="Return & Refund Policy | Mohit's Shopping App"
                />

                <meta
                    name="twitter:description"
                    content="Return and refund information for Amazon purchases."
                />

                <meta
                    name="twitter:image"
                    content="https://myonlineshopping.co.in/favicon.png"
                />

                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>

            <main className={styles.returnRefundPage}>
                {/* HERO */}
                <section className={styles.hero}>
                    <div className={styles.heroGlowOne}></div>
                    <div className={styles.heroGlowTwo}></div>

                    <Container>
                        <div className={styles.breadcrumb}>
                            <span>Home</span>
                            <i>/</i>
                            <span>Legal</span>
                            <i>/</i>
                            <strong>Return & Refund Policy</strong>
                        </div>

                        <div className={styles.heroContent}>
                            <div className={styles.heroText}>
                                <div className={styles.eyebrow}>
                                    <span className={styles.eyebrowIcon}>
                                        ↻
                                    </span>

                                    Returns & Refund Information
                                </div>

                                <h1>
                                    Return &
                                    <span> Refund Policy</span>
                                </h1>

                                <p>
                                    Important information about returns,
                                    refunds, replacements, and cancellations
                                    for Amazon purchases.
                                </p>

                                <div className={styles.heroMeta}>
                                    <div className={styles.metaItem}>
                                        <span className={styles.metaIcon}>
                                            ↻
                                        </span>

                                        <div>
                                            <small>Last updated</small>
                                            <strong>
                                                August 26, 2026
                                            </strong>
                                        </div>
                                    </div>

                                    <div className={styles.metaDivider}></div>

                                    <div className={styles.metaItem}>
                                        <span className={styles.metaIcon}>
                                            ◉
                                        </span>

                                        <div>
                                            <small>Policy type</small>
                                            <strong>
                                                Returns & Refunds
                                            </strong>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* DOCUMENT VISUAL */}
                            <div className={styles.heroVisual}>
                                <div className={styles.documentCard}>
                                    <div className={styles.documentTop}>
                                        <div className={styles.docLogo}>
                                            RR
                                        </div>

                                        <div className={styles.docLines}>
                                            <span></span>
                                            <span></span>
                                        </div>

                                        <div className={styles.checkCircle}>
                                            ✓
                                        </div>
                                    </div>

                                    <div className={styles.docTitle}>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>

                                    <div className={styles.docContent}>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>

                                    <div className={styles.returnVisual}>
                                        <div className={styles.returnCircle}>
                                            ↻
                                        </div>

                                        <div className={styles.returnArrow}>
                                            →
                                        </div>

                                        <div className={styles.returnBox}>
                                            ✓
                                        </div>
                                    </div>

                                    <div className={styles.docFooter}>
                                        <span>POLICY</span>
                                        <span>2026</span>
                                    </div>
                                </div>

                                <div className={styles.floatingBadge}>
                                    <span>✓</span>
                                    Clear & Transparent
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* CONTENT */}
                <Container className={styles.contentContainer}>
                    <div className={styles.layout}>
                        {/* SIDEBAR */}
                        <aside className={styles.sidebar}>
                            <div className={styles.sidebarInner}>
                                <div className={styles.sidebarTitle}>
                                    <span>Contents</span>
                                    <small>9 sections</small>
                                </div>

                                <nav className={styles.toc}>
                                    {sections.map((section) => (
                                        <a
                                            key={section.id}
                                            href={`#${section.id}`}
                                        >
                                            <span>{section.number}</span>
                                            <strong>
                                                {section.title}
                                            </strong>
                                        </a>
                                    ))}
                                </nav>

                                <div className={styles.helpCard}>
                                    <div className={styles.helpIcon}>
                                        ?
                                    </div>

                                    <strong>Need help?</strong>

                                    <p>
                                        Have a question about returns,
                                        refunds, or our website?
                                    </p>

                                    <a href="https://myonlineshopping.co.in/contact-us">
                                        Contact us
                                        <span>→</span>
                                    </a>
                                </div>
                            </div>
                        </aside>

                        {/* ARTICLE */}
                        <article className={styles.article}>
                            {/* IMPORTANT NOTICE */}
                            <div className={styles.notice}>
                                <div className={styles.noticeIcon}>
                                    ↻
                                </div>

                                <div className={styles.noticeContent}>
                                    <span className={styles.noticeLabel}>
                                        Important information
                                    </span>

                                    <h2>
                                        Amazon handles returns & refunds
                                    </h2>

                                    <p>
                                        Mohit's Shopping App does not process
                                        Amazon returns, refunds, replacements,
                                        or cancellations. These matters are
                                        handled directly through Amazon
                                        according to the applicable order and
                                        seller policies.
                                    </p>
                                </div>

                                <div className={styles.noticeArrow}>
                                    ↗
                                </div>
                            </div>

                            {/* SECTION 1 */}
                            <section
                                id="purchases"
                                className={styles.policySection}
                            >
                                <div className={styles.sectionNumber}>
                                    01
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>
                                        1. Purchases Are Completed on Amazon
                                    </h2>

                                    <p>
                                        Mohit's Shopping App provides product
                                        information and affiliate links.
                                    </p>

                                    <p>
                                        When you choose to purchase a product,
                                        you are redirected to Amazon where
                                        you voluntarily complete the purchase.
                                    </p>

                                    <p>
                                        We do not process your Amazon payment
                                        or create your Amazon order.
                                    </p>
                                </div>
                            </section>

                            {/* SECTION 2 */}
                            <section
                                id="returns"
                                className={styles.policySection}
                            >
                                <div className={styles.sectionNumber}>
                                    02
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>2. Return Requests</h2>

                                    <p>
                                        If you want to return a product
                                        purchased through Amazon, please use
                                        the return option available through
                                        your Amazon account or the relevant
                                        Amazon order page.
                                    </p>

                                    <p>
                                        Return eligibility and conditions are
                                        determined by Amazon and/or the
                                        applicable seller.
                                    </p>
                                </div>
                            </section>

                            {/* SECTION 3 */}
                            <section
                                id="refunds"
                                className={styles.policySection}
                            >
                                <div className={styles.sectionNumber}>
                                    03
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>3. Refunds</h2>

                                    <p>
                                        Refunds for Amazon purchases are
                                        handled by Amazon according to its
                                        applicable policies and payment
                                        procedures.
                                    </p>

                                    <p>
                                        Mohit's Shopping App does not receive
                                        your Amazon purchase payment and does
                                        not issue refunds for Amazon orders.
                                    </p>
                                </div>
                            </section>

                            {/* SECTION 4 */}
                            <section
                                id="replacement"
                                className={styles.policySection}
                            >
                                <div className={styles.sectionNumber}>
                                    04
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>
                                        4. Replacement or Damaged Products
                                    </h2>

                                    <p>
                                        If your Amazon order is damaged,
                                        defective, incomplete, incorrect, or
                                        otherwise has a product issue, please
                                        use the relevant Amazon order support,
                                        replacement, or return options.
                                    </p>
                                </div>
                            </section>

                            {/* SECTION 5 */}
                            <section
                                id="cancellation"
                                className={styles.policySection}
                            >
                                <div className={styles.sectionNumber}>
                                    05
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>5. Order Cancellation</h2>

                                    <p>
                                        If you want to cancel an Amazon order,
                                        use the cancellation options provided
                                        by Amazon for that order.
                                    </p>

                                    <p>
                                        Whether an order can be cancelled
                                        depends on the order's current status
                                        and Amazon's applicable policies.
                                    </p>
                                </div>
                            </section>

                            {/* SECTION 6 */}
                            <section
                                id="contact-amazon"
                                className={styles.policySection}
                            >
                                <div className={styles.sectionNumber}>
                                    06
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>6. Where Should You Contact?</h2>

                                    <p>
                                        For an Amazon purchase, please contact
                                        Amazon directly for:
                                    </p>

                                    <ul className={styles.issueList}>
                                        <li>Return requests</li>
                                        <li>Refund requests</li>
                                        <li>Replacement requests</li>
                                        <li>Order cancellation</li>
                                        <li>Damaged products</li>
                                        <li>Missing products</li>
                                        <li>Delivery problems</li>
                                        <li>Payment or order issues</li>
                                    </ul>

                                    <p>
                                        You should use the support and order
                                        management options available through
                                        Amazon for these matters.
                                    </p>
                                </div>
                            </section>

                            {/* SECTION 7 */}
                            <section
                                id="role"
                                className={styles.policySection}
                            >
                                <div className={styles.sectionNumber}>
                                    07
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>7. Our Role</h2>

                                    <p>
                                        Our role is limited to helping
                                        visitors discover products and
                                        providing affiliate links to
                                        third-party shopping platforms.
                                    </p>

                                    <p>
                                        We do not control Amazon's sellers,
                                        inventory, payment processing,
                                        delivery, return eligibility, refund
                                        decisions, or customer service.
                                    </p>
                                </div>
                            </section>

                            {/* SECTION 8 */}
                            <section
                                id="amazon-policies"
                                className={styles.policySection}
                            >
                                <div className={styles.sectionNumber}>
                                    08
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>8. Amazon Policies Apply</h2>

                                    <p>
                                        Return, refund, replacement,
                                        cancellation, seller, and order
                                        policies may vary depending on the
                                        product, seller, customer location,
                                        order status, and other factors.
                                    </p>

                                    <p>
                                        Always review the current information
                                        provided by Amazon before purchasing.
                                    </p>
                                </div>
                            </section>

                            {/* SECTION 9 */}
                            <section
                                id="support"
                                className={styles.policySection}
                            >
                                <div className={styles.sectionNumber}>
                                    09
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>9. Website Support</h2>

                                    <p>
                                        You can contact us for questions
                                        regarding our website, product
                                        discovery features, or affiliate
                                        links.
                                    </p>

                                    <div className={styles.contactActions}>
                                        <a
                                            href="mailto:mahi8191singh@gmail.com"
                                            className={styles.contactButton}
                                        >
                                            <span
                                                className={
                                                    styles.buttonIcon
                                                }
                                            >
                                                ✉
                                            </span>

                                            <span>
                                                <small>Email us</small>
                                                <strong>
                                                    mahi8191singh@gmail.com
                                                </strong>
                                            </span>

                                            <b>→</b>
                                        </a>

                                        <a
                                            href="tel:+918191042065"
                                            className={styles.contactButton}
                                        >
                                            <span
                                                className={
                                                    styles.buttonIcon
                                                }
                                            >
                                                ☎
                                            </span>

                                            <span>
                                                <small>Call us</small>
                                                <strong>
                                                    +91 8191042065
                                                </strong>
                                            </span>

                                            <b>→</b>
                                        </a>
                                    </div>
                                </div>
                            </section>

                            {/* BOTTOM NOTE */}
                            <div className={styles.bottomNote}>
                                <span>✓</span>

                                For Amazon order-related issues, please use
                                Amazon's support and order-management options.
                            </div>
                        </article>
                    </div>
                </Container>
            </main>
        </>
    );
};

export default ReturnRefundPolicy;
